import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Nav from "@/components/Nav";
import { toast } from "sonner";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(form.email, form.password, form.name);
      toast.success("Account provisioned");
      nav("/scan");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="grain min-h-screen bg-[#050505] text-white">
      <Nav />
      <div className="mx-auto grid min-h-[calc(100vh-73px)] max-w-7xl grid-cols-1 md:grid-cols-2">
        <div className="hidden flex-col justify-between border-r border-white/10 p-12 md:flex">
          <div>
            <div className="mb-4 h-8 w-8 rotate-45 border border-[#E5FF00] bg-[#E5FF00]/10"></div>
            <h2 className="font-display text-4xl font-medium tracking-tighter">Enroll into<br/>the vault.</h2>
            <p className="mt-4 max-w-md text-zinc-400">One account. One AI wizard. 400+ brokers on the run.</p>
          </div>
          <ul className="space-y-3 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
            <li>[✓] Free exposure scan</li>
            <li>[✓] AI-generated removal plan</li>
            <li>[✓] Encrypted vault storage</li>
          </ul>
        </div>

        <div className="flex flex-col justify-center p-8 md:p-12">
          <div className="mx-auto w-full max-w-sm">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#E5FF00]">/ NEW OPERATIVE</div>
            <h1 className="font-display text-3xl font-medium tracking-tight">Create account</h1>

            <form onSubmit={submit} className="mt-10 space-y-6">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Name</Label>
                <Input data-testid="signup-name" required value={form.name} onChange={set("name")}
                  className="mt-2 rounded-none border-white/10 bg-black font-mono text-sm focus-visible:border-[#E5FF00] focus-visible:ring-0" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Email</Label>
                <Input data-testid="signup-email" type="email" required value={form.email} onChange={set("email")}
                  className="mt-2 rounded-none border-white/10 bg-black font-mono text-sm focus-visible:border-[#E5FF00] focus-visible:ring-0" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Password</Label>
                <Input data-testid="signup-password" type="password" required minLength={6} value={form.password} onChange={set("password")}
                  className="mt-2 rounded-none border-white/10 bg-black font-mono text-sm focus-visible:border-[#E5FF00] focus-visible:ring-0" />
              </div>
              <Button data-testid="signup-submit" disabled={loading} type="submit"
                className="btn-cta h-12 w-full rounded-none border border-[#E5FF00] bg-[#E5FF00] font-mono text-[11px] uppercase tracking-[0.25em] text-black hover:bg-transparent hover:text-[#E5FF00]">
                {loading ? "Provisioning…" : "Provision Account →"}
              </Button>
            </form>
            <p className="mt-8 font-mono text-[11px] text-zinc-500">
              Have an account? <Link data-testid="link-login" to="/login" className="text-[#E5FF00] hover:underline">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
